import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Avatar from '@mui/material/Avatar';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiConfigCached } from '../../config/api.config';
import { useTraderData } from '../../contexts/TraderDataContext';
import { usePriceUpdates } from '../../hooks/usePriceUpdates';
import {
  calculateRequiredSpendByExchange,
} from '../../utils/assetStatsBudget';
import { getUsdtKrwRate } from '../../utils/currencyConversion';
import {
  buildTraderApiBasePath,
  getTraderRouteVersion,
} from '../../utils/traderRouting';

/**
 * Asset balance from the API
 */
interface AssetBalance {
  exchange: string;
  asset: string;
  balance: number;
  locked: number;
  total: number;
}

/**
 * Balances response from the API
 */
interface BalancesResponse {
  trader_name: string;
  balances: AssetBalance[];
}

/**
 * Exchange quote asset configuration
 */
interface ExchangeQuoteConfig {
  exchange: string;
  displayName: string;
  quoteAsset: string;
  logoPath: string;
  isFutures?: boolean;
  /** The exchange key used by the API for balance lookup */
  balanceExchange: string;
}

/**
 * Exchange configurations for display
 * Note: balanceExchange is the key used by the API for balance lookup
 * exchange is the display/component key (also used for position lookup)
 */
const EXCHANGE_CONFIGS: ExchangeQuoteConfig[] = [
  { exchange: 'Upbit', displayName: 'Upbit', quoteAsset: 'KRW', logoPath: '/logos/upbit.png', balanceExchange: 'Upbit' },
  { exchange: 'Bithumb', displayName: 'Bithumb', quoteAsset: 'KRW', logoPath: '/logos/bithumb.ico', balanceExchange: 'Bithumb' },
  { exchange: 'BinanceFutures', displayName: 'Binance Futures', quoteAsset: 'USDT', logoPath: '/logos/binance.png', isFutures: true, balanceExchange: 'BinanceFutures' },
  { exchange: 'Binance', displayName: 'Binance', quoteAsset: 'USDT', logoPath: '/logos/binance.png', balanceExchange: 'Binance' },
];

function toDisplayExchangeName(exchangeName: string | null | undefined): string | null {
  if (!exchangeName) return null;
  if (exchangeName.endsWith('Futures')) return exchangeName;
  const normalized = exchangeName.toLowerCase();
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'upbit') return 'Upbit';
  if (normalized === 'bithumb') return 'Bithumb';
  return exchangeName;
}

/**
 * Format currency value based on the asset type
 */
function formatBalance(value: number, asset: string): string {
  if (asset === 'KRW') {
    return new Intl.NumberFormat('ko-KR', {
      maximumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);
}

/**
 * Get currency symbol
 */
function getCurrencySymbol(asset: string): string {
  switch (asset) {
    case 'KRW':
      return '₩';
    case 'USDT':
    case 'USD':
      return '$';
    case 'BTC':
      return '₿';
    default:
      return '';
  }
}

/**
 * AssetStats - Display exchange asset balances and position spent amounts
 *
 * Shows:
 * - Exchange key quote assets (Upbit: KRW, Bithumb: KRW, Binance: USDT, Binance Futures: USDT)
 * - Amount spent per exchange for current positions
 */
export const AssetStats: React.FC = () => {
  const { t } = useTranslation();
  const { trader_name } = useParams<{ trader_name: string }>();
  const location = useLocation();
  const { strategies } = useTraderData();
  const priceData = usePriceUpdates();
  const usdtKrwRate = getUsdtKrwRate(priceData);

  const [balances, setBalances] = useState<AssetBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Transfer Dialog State
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferDirection, setTransferDirection] = useState('');
  const [transferType, setTransferType] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [availableBalance, setAvailableBalance] = useState<number>(0);

  /**
   * Get the API base URL
   */
  const getApiBaseUrl = useCallback(() => {
    const apiConfig = getApiConfigCached();
    const apiBasePath = buildTraderApiBasePath(
      trader_name || 'njovy',
      getTraderRouteVersion(location.pathname)
    );
    if (apiConfig.isProduction) {
      return `${window.location.protocol}//${apiConfig.host}${apiBasePath}`;
    } else {
      const port = apiConfig.wsPort || 8765;
      return `http://${apiConfig.host}:${port}${apiBasePath}`;
    }
  }, [location.pathname, trader_name]);

  /**
   * Fetch balances from the API
   */
  const fetchBalances = useCallback(async (refresh: boolean = false) => {
    if (!trader_name) return;

    try {
      if (refresh) {
        setRefreshing(true);
      }

      const url = refresh
        ? `${getApiBaseUrl()}/balances?refresh=true`
        : `${getApiBaseUrl()}/balances`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: BalancesResponse = await response.json();
      setBalances(data.balances);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('[AssetStats] Failed to fetch balances:', errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [trader_name, getApiBaseUrl]);

  // Initial fetch
  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  // Auto-update every 10 seconds (without force refresh)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBalances(false);
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchBalances]);

  /**
   * Handle refresh button click
   */
  const handleRefresh = useCallback(() => {
    fetchBalances(true);
  }, [fetchBalances]);

  /**
   * Calculate net position amounts per exchange from slim v2 strategy exposures.
   */
  const spentByExchange = useMemo(() => {
    const positions: Record<string, { amount: number; quote: string }> = {};

    for (const strategy of strategies) {
      const spotExchange = toDisplayExchangeName(strategy.spot_exchange_name);
      if (spotExchange && strategy.spot_quote_asset) {
        if (!positions[spotExchange]) {
          positions[spotExchange] = { amount: 0, quote: strategy.spot_quote_asset };
        }
        positions[spotExchange].amount += strategy.spot_net_quote_exposure;
      }

      const futuresExchange = toDisplayExchangeName(strategy.futures_exchange_name);
      if (futuresExchange) {
        if (!positions[futuresExchange]) {
          positions[futuresExchange] = { amount: 0, quote: 'USDT' };
        }
        positions[futuresExchange].amount += strategy.futures_net_notional_usdt;
      }
    }

    return positions;
  }, [strategies]);

  const requiredSpendByExchange = useMemo(
    () => calculateRequiredSpendByExchange(
      strategies.map((strategy) => ({
        currentState: strategy.current_state,
        exchange: toDisplayExchangeName(strategy.spot_exchange_name),
        quoteAsset: strategy.spot_quote_asset ?? strategy.buy_quote,
        budgetUsdt: strategy.budget_usdt,
        spentQuote: strategy.spot_net_quote_exposure,
      })),
      usdtKrwRate,
    ),
    [strategies, usdtKrwRate],
  );

  /**
   * Get available balance for a specific exchange and asset
   * Always returns available balance (excludes locked/in-order amounts)
   */
  const getBalance = useCallback((exchange: string, asset: string): number | null => {
    const balance = balances.find(
      b => b.exchange === exchange && b.asset === asset
    );
    if (!balance) return null;
    // Always use available balance (balance.balance), not total
    return balance.balance;
  }, [balances]);

  /**
   * Get spent amount for a specific exchange
   */
  const getSpent = useCallback((exchange: string): { amount: number; quote: string } | null => {
    return spentByExchange[exchange] ?? null;
  }, [spentByExchange]);

  const getRequiredSpend = useCallback((exchange: string): { amount: number; quote: string } | null => {
    return requiredSpendByExchange[exchange] ?? null;
  }, [requiredSpendByExchange]);

  /**
   * Calculate total balances by quote asset (KRW and USDT)
   */
  const totalBalances = useMemo(() => {
    const totals: Record<string, number> = { KRW: 0, USDT: 0 };

    for (const config of EXCHANGE_CONFIGS) {
      const balance = balances.find(
        b => b.exchange === config.balanceExchange && b.asset === config.quoteAsset
      );
      if (balance) {
        // Use available balance, not total
        totals[config.quoteAsset] = (totals[config.quoteAsset] || 0) + balance.balance;
      }
    }

    return totals;
  }, [balances]);

  /**
   * Calculate total position amount (sum of all spent)
   */
  const totalPosition = useMemo(() => {
    const totals: Record<string, number> = { KRW: 0, USDT: 0 };

    for (const config of EXCHANGE_CONFIGS) {
      const spent = spentByExchange[config.exchange];
      if (spent && spent.amount > 0) {
        totals[spent.quote] = (totals[spent.quote] || 0) + spent.amount;
      }
    }

    return totals;
  }, [spentByExchange]);

  const totalRequiredSpend = useMemo(() => {
    const totals: Record<string, number> = { KRW: 0, USDT: 0 };

    for (const requiredSpend of Object.values(requiredSpendByExchange)) {
      totals[requiredSpend.quote] += requiredSpend.amount;
    }

    return totals;
  }, [requiredSpendByExchange]);

  if (loading) {
    return (
      <Paper
        elevation={4}
        sx={{
          mb: 2,
          mx: { xs: 2, sm: 3, md: 4 },
          p: 1.5,
          background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.05) 0%, rgba(255, 100, 0, 0.05) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 165, 0, 0.2)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 60,
        }}
      >
        <CircularProgress size={20} sx={{ color: 'rgba(255, 165, 0, 0.7)' }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={4}
      sx={{
        mb: 2,
        mx: { xs: 2, sm: 3, md: 4 },
        background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.05) 0%, rgba(255, 100, 0, 0.05) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 165, 0, 0.2)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1, position: 'relative' }}>
        {/* Compact Header with Refresh Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccountBalanceWalletIcon sx={{ color: 'rgba(255, 165, 0, 0.7)', fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 165, 0, 0.9)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              >
                {t('assetStats.title', 'Balances')}:
              </Typography>
            </Box>
            {/* Total KRW */}
            {totalBalances.KRW > 0 && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: '0.7rem',
                }}
              >
                ₩{formatBalance(totalBalances.KRW, 'KRW')}
                {totalPosition.KRW > 0 && (
                  <Box component="span" sx={{ color: 'warning.main', ml: 0.5 }}>
                    ({formatBalance(totalPosition.KRW, 'KRW')})
                  </Box>
                )}
                {totalRequiredSpend.KRW > 0 && (
                  <Box
                    component="span"
                    aria-label={t('assetStats.remainingToSpend')}
                    sx={{ color: 'info.main', ml: 0.5, whiteSpace: 'nowrap' }}
                  >
                    (⏳{formatBalance(totalRequiredSpend.KRW, 'KRW')})
                  </Box>
                )}
              </Typography>
            )}
            {/* Total USDT */}
            {totalBalances.USDT > 0 && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: 'text.primary',
                  fontSize: '0.7rem',
                }}
              >
                ${formatBalance(totalBalances.USDT, 'USDT')}
                {totalPosition.USDT > 0 && (
                  <Box component="span" sx={{ color: 'warning.main', ml: 0.5 }}>
                    ({formatBalance(totalPosition.USDT, 'USDT')})
                  </Box>
                )}
                {totalRequiredSpend.USDT > 0 && (
                  <Box
                    component="span"
                    aria-label={t('assetStats.remainingToSpend')}
                    sx={{ color: 'info.main', ml: 0.5, whiteSpace: 'nowrap' }}
                  >
                    (⏳{formatBalance(totalRequiredSpend.USDT, 'USDT')})
                  </Box>
                )}
              </Typography>
            )}
          </Box>
          <Tooltip title={t('assetStats.refresh', 'Refresh from exchanges')} arrow>
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                sx={{
                  p: 0.25,
                  color: 'rgba(255, 165, 0, 0.7)',
                  '&:hover': {
                    color: 'rgba(255, 165, 0, 1)',
                    backgroundColor: 'rgba(255, 165, 0, 0.1)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 165, 0, 0.3)',
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 16,
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Error State */}
        {error && (
          <Typography
            variant="caption"
            sx={{ color: 'error.main', display: 'block', mb: 1 }}
          >
            {error}
          </Typography>
        )}

        {/* Exchange Grid - Compact layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1,
          }}
        >
          {EXCHANGE_CONFIGS.map((config) => {
            // Use balanceExchange for API lookup, exchange for position lookup
            const balance = getBalance(config.balanceExchange, config.quoteAsset);
            const spent = getSpent(config.exchange);
            const requiredSpend = getRequiredSpend(config.exchange);
            const symbol = getCurrencySymbol(config.quoteAsset);

            // Binance transfer support
            const isBinance = config.exchange === 'Binance' || config.exchange === 'BinanceFutures';

            const handleExchangeClick = () => {
              if (!isBinance) return;

              const isFutures = config.exchange === 'BinanceFutures';
              // Tapping an exchange = recharge funds TO that exchange FROM the other
              // If clicking Futures, we want to move FROM Spot TO Futures (MainToUmFuture) to recharge Futures
              // If clicking Spot (Binance), we want to move FROM Futures TO Spot (UmFutureToMain) to recharge Spot
              // NOTE: Only these two transfer types are supported by our API (USDT between Spot <-> Futures)
              const type = isFutures ? 'MainToUmFuture' : 'UmFutureToMain';
              const direction = isFutures ? 'Spot → Futures' : 'Futures → Spot';

              setTransferType(type);
              setTransferDirection(direction);
              setTransferAmount('');

              // Set available balance for validation (source is the OTHER exchange)
              const sourceExchange = isFutures ? 'Binance' : 'BinanceFutures';
              const avail = getBalance(sourceExchange, 'USDT') || 0;
              setAvailableBalance(avail);

              setTransferDialogOpen(true);
            };

            return (
              <Tooltip key={config.exchange} title={isBinance ? `${config.displayName} (Click to recharge USDT)` : config.displayName} arrow placement="top">
                <Box
                  onClick={handleExchangeClick}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    background: 'rgba(0, 0, 0, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    cursor: isBinance ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    '&:hover': isBinance ? {
                      background: 'rgba(255, 165, 0, 0.1)',
                      borderColor: 'rgba(255, 165, 0, 0.3)',
                      transform: 'translateY(-2px)'
                    } : {}
                  }}
                >
                  {/* Exchange Logo Only */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.75 }}>
                    <Box
                      component="img"
                      src={config.logoPath}
                      alt={config.displayName}
                      sx={{
                        height: 18,
                        width: 'auto',
                        opacity: 0.9,
                        // Grayscale for futures to differentiate from spot
                        filter: config.isFutures ? 'grayscale(100%) brightness(1.8)' : 'none',
                      }}
                    />
                  </Box>

                  {/* Balance */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      color: balance !== null ? 'text.primary' : 'text.disabled',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {balance !== null
                      ? `${symbol}${formatBalance(balance, config.quoteAsset)}`
                      : '-'}
                  </Typography>

                  {/* Spent Amount */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      color: spent ? 'warning.main' : 'text.disabled',
                      fontSize: '0.7rem',
                      display: 'block',
                      textAlign: 'center',
                      mt: 0.25,
                    }}
                  >
                    {spent
                      ? `(${getCurrencySymbol(spent.quote)}${formatBalance(spent.amount, spent.quote)})`
                      : '(-)'}
                    {requiredSpend && (
                      <Box
                        component="span"
                        aria-label={t('assetStats.remainingToSpend')}
                        sx={{ color: 'info.main', ml: 0.5, whiteSpace: 'nowrap' }}
                      >
                        (⏳{getCurrencySymbol(requiredSpend.quote)}{formatBalance(requiredSpend.amount, requiredSpend.quote)})
                      </Box>
                    )}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* Transfer Dialog */}
      <Dialog
        open={transferDialogOpen}
        onClose={() => !transferLoading && setTransferDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(15,18,28,0.95) 0%, rgba(9,12,20,0.98) 100%)',
            border: '1px solid rgba(255, 165, 0, 0.2)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {t('assetStats.transferTitle', 'Universal Transfer')}
            </Typography>
          </Box>
          {/* Visual transfer direction with logos */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
            {/* Source */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src="/logos/binance.png"
                sx={{
                  width: 32,
                  height: 32,
                  // Futures = grayscale, Spot = normal
                  filter: transferType === 'UmFutureToMain' ? 'grayscale(100%) brightness(1.5)' : 'none',
                  border: '2px solid',
                  borderColor: transferType === 'UmFutureToMain' ? 'rgba(255,255,255,0.3)' : 'warning.main',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                }}
              >
                {transferType === 'UmFutureToMain' ? 'Futures' : 'Spot'}
              </Typography>
            </Box>
            {/* Arrow */}
            <ArrowForwardIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            {/* Destination */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src="/logos/binance.png"
                sx={{
                  width: 32,
                  height: 32,
                  // Futures = grayscale, Spot = normal
                  filter: transferType === 'MainToUmFuture' ? 'grayscale(100%) brightness(1.5)' : 'none',
                  border: '2px solid',
                  borderColor: transferType === 'MainToUmFuture' ? 'rgba(255,255,255,0.3)' : 'warning.main',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                }}
              >
                {transferType === 'MainToUmFuture' ? 'Futures' : 'Spot'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label={t('assetStats.amount', 'Amount')}
              type="number"
              fullWidth
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              disabled={transferLoading}
              InputProps={{
                endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'warning.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'warning.main',
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setTransferDialogOpen(false)}
            disabled={transferLoading}
            sx={{ color: 'text.secondary' }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={async () => {
              const amount = parseFloat(transferAmount);
              if (isNaN(amount) || amount <= 0) {
                // Ideally use a snackbar, relying on browser validation/visual cue for now
                return;
              }

              try {
                setTransferLoading(true);
                const response = await fetch(`${getApiBaseUrl()}/universal_transfer`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    transfer_type: transferType,
                    asset: 'USDT',
                    amount: amount
                  }),
                });

                if (!response.ok) {
                  const errData = await response.json();
                  throw new Error(errData.message || 'Transfer failed');
                }

                // Success
                handleRefresh();
                setTransferDialogOpen(false);
                // Optional: success notification

              } catch (e) {
                alert(`Transfer error: ${e instanceof Error ? e.message : String(e)}`);
              } finally {
                setTransferLoading(false);
              }
            }}
            variant="contained"
            disabled={
              transferLoading ||
              !transferAmount ||
              parseFloat(transferAmount) <= 0 ||
              parseFloat(transferAmount) > availableBalance
            }
            color="warning"
            startIcon={transferLoading ? <CircularProgress size={16} /> : <SwapHorizIcon />}
          >
            {t('assetStats.transferAction', 'Transfer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper >
  );
};

/**
 * Props for AssetStatsInline component
 */
interface AssetStatsInlineProps {
  /** Callback when expand button is clicked */
  onExpand: () => void;
}

/**
 * AssetStatsInline - Compact inline display for minimized header
 *
 * Shows exchange logos with their quote asset balances in a single row
 */
export const AssetStatsInline: React.FC<AssetStatsInlineProps> = ({ onExpand }) => {
  const { t } = useTranslation();
  const { trader_name } = useParams<{ trader_name: string }>();
  const location = useLocation();
  const [balances, setBalances] = useState<AssetBalance[]>([]);

  /**
   * Get the API base URL
   */
  const getApiBaseUrl = useCallback(() => {
    const apiConfig = getApiConfigCached();
    const apiBasePath = buildTraderApiBasePath(
      trader_name || 'njovy',
      getTraderRouteVersion(location.pathname)
    );
    if (apiConfig.isProduction) {
      return `${window.location.protocol}//${apiConfig.host}${apiBasePath}`;
    } else {
      const port = apiConfig.wsPort || 8765;
      return `http://${apiConfig.host}:${port}${apiBasePath}`;
    }
  }, [location.pathname, trader_name]);

  /**
   * Fetch balances from the API
   */
  useEffect(() => {
    if (!trader_name) return;

    const fetchBalances = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/balances`);
        if (response.ok) {
          const data: BalancesResponse = await response.json();
          setBalances(data.balances);
        }
      } catch (err) {
        console.error('[AssetStatsInline] Failed to fetch balances:', err);
      }
    };

    fetchBalances();
  }, [trader_name, getApiBaseUrl]);

  /**
   * Get balance for a specific exchange and asset
   */
  const getBalance = useCallback((exchange: string, asset: string): number | null => {
    const balance = balances.find(
      b => b.exchange === exchange && b.asset === asset
    );
    return balance?.total ?? null;
  }, [balances]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 1 },
        px: { xs: 0.75, sm: 1 },
        py: 0.5,
        borderRadius: 1,
        background: 'rgba(255, 165, 0, 0.05)',
        border: '1px solid rgba(255, 165, 0, 0.2)',
      }}
    >
      {EXCHANGE_CONFIGS.map((config) => {
        // Use balanceExchange for API lookup
        const balance = getBalance(config.balanceExchange, config.quoteAsset);
        const symbol = getCurrencySymbol(config.quoteAsset);

        return (
          <Box key={config.exchange} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              component="img"
              src={config.logoPath}
              alt={config.displayName}
              sx={{
                height: { xs: 12, sm: 14 },
                width: 'auto',
                filter: config.isFutures ? 'grayscale(100%) brightness(1.8)' : 'none',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                color: balance !== null ? 'text.primary' : 'text.disabled',
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
              }}
            >
              {balance !== null ? `${symbol}${formatBalance(balance, config.quoteAsset)}` : '-'}
            </Typography>
          </Box>
        );
      })}

      {/* Expand Button */}
      <Tooltip title={t('assetStats.expand', 'Show details')} arrow>
        <IconButton
          onClick={onExpand}
          size="small"
          sx={{
            p: 0.25,
            color: 'rgba(255, 165, 0, 0.7)',
            '&:hover': {
              color: 'rgba(255, 165, 0, 1)',
              backgroundColor: 'rgba(255, 165, 0, 0.1)',
            },
          }}
        >
          <AccountBalanceWalletIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default AssetStats;
